import { produce } from 'immer';
import { DateRangeFilterStore, DateRangeIntervals, KeyToFilterConfig, KeyToFilterStore, ListFilterStore, MultipleAccountsFilterStore } from './types';
import { create } from 'zustand';
import { filtersConfigToStore } from './dashboard-filters.utils';
import { storeFilters } from '../../saved-filters/store';
import { getFilters } from '../../saved-filters/get';
import { removeCurrentView } from '../../saved-filters/remove-current-view';
import { setCurrentView } from '../../saved-filters/set-current-view';
import { SavedFiltersFilterItem } from '../../saved-filters/types';
import { hasCurrentView } from '../../saved-filters/has-current-view';
import { getCurrentView } from '../../saved-filters/get-current-view';
import { ParticipantFilter } from '../../../API/types';

export const PORTFOLIO_INSIGHTS_PAGE_KEY = 'portfolio-insights';

interface DashboardFiltersState {
  filters: KeyToFilterStore;
  refetchMethods: Record<string, VoidFunction>;
  currentFilterView: string;
  dateFilterChanged: (filterId: string, intervals: DateRangeIntervals) => void;
  listFilterSelectionChanged: (filterId: string, optionIds: string[]) => void;
  currencyFilterSelectionChanged: (filterId: string, from: number, to: number) => void;
  crmNumberFilterSelectionChanged: (filterId: string, from: number, to: number) => void;
  dateOptionSelectionChanged: (filterId: string, option: string) => void;
  dateFilterSelectionChanged: (filterId: string, dates: [Date, Date]) => void;
  accountFilterSelectionChanged: (filterId: string, selected: string | undefined) => void;
  multipleAccountsFilterSelectionChanged: (filterId: string, selected: string[]) => void;
  participantsFilterSelectionChanged: (filterId: string, selected: ParticipantFilter[]) => void;
  numberFilterValueChanged: (filterId: string, value: number | null) => void;
  rangeNumberFilterValueChanged: (filterId: string, value: [number, number]) => void;
  selectFilterSelectionChanged: (filterId: string, selected: number | string) => void;
  meetingTypeChanged: (filterId: string, selected: string | null) => void;
  filtersChanged: (filters: KeyToFilterConfig) => void;
  triggerDataLoad: (key?: string) => void;
  clearFilters: VoidFunction;
  hasRefetchMethod: (key: string) => boolean;
  setRefetchMethod: (key: string, method: VoidFunction) => void;
  saveFiltersView: (viewName: string) => void;
  setCurrentFilterView: (viewId: string | null) => void;
}

export type DashboardFiltersStore = ReturnType<typeof createStore>;

type CreateStoreArgs = {
  filters: KeyToFilterConfig;
};

const getStoredFilters = (viewId: string) => {
  const storedFilters = getFilters({ pageKey: PORTFOLIO_INSIGHTS_PAGE_KEY, viewId });
  setCurrentView(PORTFOLIO_INSIGHTS_PAGE_KEY, viewId);

  const keyFilters: KeyToFilterConfig = {
    multipleAccounts: {
      type: 'multipleAccounts',
      config: {
        selected: storedFilters.multipleAccounts as string[]
      }
    },
    dateRange: {
      type: 'dateRange',
      config: {
        value: storedFilters.dateRangeType as string,
        interval: [
          new Date((storedFilters.dateRange as [string, string])[0]),
          new Date((storedFilters.dateRange as [string, string])[1])
        ]
      }
    },
  }

  return {
    keyFilters: keyFilters,
    filters: storedFilters
  };
}

export const createStore = (args: CreateStoreArgs) => {
  let filters: KeyToFilterStore;

  if (hasCurrentView(PORTFOLIO_INSIGHTS_PAGE_KEY)) {
    const viewId = getCurrentView(PORTFOLIO_INSIGHTS_PAGE_KEY);
    const { keyFilters } = getStoredFilters(viewId);
    filters = filtersConfigToStore(keyFilters);
  } else {
    filters = filtersConfigToStore(args.filters);
  }

  return create<DashboardFiltersState>((set, get) => {
    return {
      filters,
      refetchMethods: {},
      currentFilterView: '',
      hasRefetchMethod: key => get().refetchMethods[key] !== undefined,
      dateFilterChanged: (filterId, intervals) => {
        set(prevState =>
          produce(prevState, draft => {
            const filter = draft.filters[filterId];
            if (filter && filter.type === 'dateRange') {
              filter.value.interval = intervals;
            }
          }),
        );
      },
      rangeNumberFilterValueChanged: (filterId, intervals) => {
        set(prevState =>
          produce(prevState, draft => {
            const filter = draft.filters[filterId];
            if (filter && filter.type === 'numberRange') {
              filter.value.interval = intervals;
            }
          }),
        );
      },
      filtersChanged: filters => {
        set({ filters: filtersConfigToStore(filters) });
      },
      listFilterSelectionChanged: (filterId, optionIds) => {
        set(prevState =>
          produce(prevState, draft => {
            const filter = draft.filters[filterId];
            if (filter && filter.type === 'list') {
              filter.value.options = filter.value.options.map(option => {
                option.selected = optionIds.includes(option.id);
                return option;
              });
            }
          }),
        );
      },
      currencyFilterSelectionChanged: (filterId, from, to) => {
        set(prevState =>
          produce(prevState, draft => {
            const filter = draft.filters[filterId];
            if (filter && filter.type === 'list' && filter.componentProps?.type === 'currency') {
              filter.value.from = from;
              filter.value.to = to;
            }
          }),
        );
      },
      crmNumberFilterSelectionChanged: (filterId, from, to) => {
        set(prevState =>
          produce(prevState, draft => {
            const filter = draft.filters[filterId];
            if (filter && filter.type === 'list' && filter.componentProps?.type === 'number') {
              filter.value.from = from;
              filter.value.to = to;
            }
          }),
        );
      },
      accountFilterSelectionChanged: (filterId, selected) => {
        set(prevState =>
          produce(prevState, draft => {
            const filter = draft.filters[filterId];
            if (filter && filter.type === 'account') {
              filter.value.selected = selected;
            }
          }),
        );
      },
      multipleAccountsFilterSelectionChanged: (filterId, selected) => {
        set(prevState =>
          produce(prevState, draft => {
            const filter = draft.filters[filterId];
            if (filter && filter.type === 'multipleAccounts') {
              filter.value.selected = selected;
            }
          }),
        );
      },
      participantsFilterSelectionChanged: (filterId, selected) => {
        set(prevState =>
          produce(prevState, draft => {
            const filter = draft.filters[filterId];
            if (filter && filter.type === 'participants') {
              filter.value.selected = selected;
            }
          }),
        );
      },
      dateOptionSelectionChanged: (filterId, option) => {
        set(prevState =>
          produce(prevState, draft => {
            const filter = draft.filters[filterId];
            if (filter && filter.type === 'dateRange') {
              filter.value.dateOptionValue = option;
            }
          }),
        );
      },
      dateFilterSelectionChanged: (filterId, dates) => {
        set(prevState =>
          produce(prevState, draft => {
            const filter = draft.filters[filterId];
            if (filter && filter.type === 'dateRange') {
              filter.value.interval = dates;
            }
          }),
        );
      },
      numberFilterValueChanged: (filterId, value) => {
        set(prevState =>
          produce(prevState, draft => {
            const filter = draft.filters[filterId];
            if (filter && filter.type === 'number') {
              filter.value.value = value;
            }
          }),
        );
      },
      selectFilterSelectionChanged: (filterId, selected) => {
        set(prevState =>
          produce(prevState, draft => {
            const filter = draft.filters[filterId];
            if (filter && filter.type === 'select') {
              filter.value.selected = selected;
            }
          }),
        );
      },
      meetingTypeChanged: (filterId, selected) => {
        set(prevState =>
          produce(prevState, draft => {
            const filter = draft.filters[filterId];
            if (filter && filter.type === 'meetingType') {
              filter.value.selected = selected;
            }
          }),
        );
      },
      triggerDataLoad: (key?: string) => {
        if (key && get().refetchMethods[key]) {
          get().refetchMethods[key]();
          return;
        }

        Object.values(get().refetchMethods).forEach(method => method());
      },
      setRefetchMethod: (key, method) => {
        set(prevState =>
          produce(prevState, draft => {
            draft.refetchMethods[key] = method;
          }),
        );
      },
      clearFilters: () => {
        set(prevState =>
          produce(prevState, draft => {
            Object.keys(draft.filters).forEach(key => {
              const filter = draft.filters[key];
              if (filter.type === 'list') {
                filter.value.options = filter.value.options.map(option => {
                  option.selected = false;
                  return option;
                });
              } else if (filter.type === 'dateRange') {
                filter.value.interval = [null, null];
                filter.value.value = '30d';
                filter.value.dateOptionValue = '30d'
              } else if (filter.type === 'multipleAccounts') {
                filter.value.selected = [];
              } else if (filter.type === 'number') {
                filter.value.value = null;
              } else if (filter.type === 'select') {
                filter.value.selected = -1;
              } else if (filter.type === 'meetingType') {
                filter.value.selected = null;
              }
            });
          }),
        );

        get().triggerDataLoad()
      },
      saveFiltersView: (viewName: string) => {
        const state = get();
        const filters: Record<string, SavedFiltersFilterItem> = {
          multipleAccounts: {
            label: 'Accounts',
            type: 'multipleAccounts',
            value: (state.filters.multipleAccounts as MultipleAccountsFilterStore).value.selected
          },
          dateRange: {
            label: 'Date Range',
            type: 'dateRange',
            value: (state.filters.dateRange as DateRangeFilterStore).value.interval
          },
          dateRangeType: {
            label: 'Date Range Type',
            type: 'dateRangeType',
            value: (state.filters.dateRange as DateRangeFilterStore).value.dateOptionValue
          },
        }

        const crmFilters = Object.entries(state.filters)
          .filter(([key, value]) => key.startsWith('crm_'))
          .reduce((acc, [key, value]) => {
            acc[key] = {
              label: `${value.componentProps?.label} (CRM)`,
              type: `crm_${value.componentProps?.type}`,
              value: (value as ListFilterStore).value.options
                .filter(option => option.selected)
                .map(option => option.id)
            }
            return acc;
          }, {} as Record<string, SavedFiltersFilterItem>)

        const allFilters = {
          ...filters,
          ...crmFilters
        }

        storeFilters({ pageKey: PORTFOLIO_INSIGHTS_PAGE_KEY, pageTitle: 'VoC Topic Discovery', viewName, filters: allFilters });
      },
      setCurrentFilterView: (viewId: string | null) => {
        if (!viewId) {
          removeCurrentView(PORTFOLIO_INSIGHTS_PAGE_KEY);
          const { refetchMethods, clearFilters } = get()
          Object.values(refetchMethods).forEach(method => method());
          if (clearFilters) clearFilters();
          set({
            currentFilterView: '',
          });
          return;
        }

        const { filters, keyFilters } = getStoredFilters(viewId);

        const state = get()

        const crmFilters = Object.entries(filters)
          .filter(([key]) => key.startsWith('crm_') && state.filters[key])
          .reduce((acc, [key, value]) => {
            acc[key] = {
              ...state.filters[key],
              value: {
                options: (state.filters[key].value as ListFilterStore['value']).options.map(option => ({
                  id: option.id,
                  label: option.label,
                  selected: (value as string[]).some((selected: string) => selected === option.id)
                }))
              }
            } as ListFilterStore;
            return acc;
          }, {} as Record<string, ListFilterStore>);

        set({
          currentFilterView: viewId,
          filters: {
            ...state.filters,
            ...filtersConfigToStore(keyFilters),
            ...crmFilters,
          },
        })
      },
    };
  });
};
